import React, { useMemo } from 'react';
import { Card, ListGroup, Badge, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from '../../common/Flex';

const ClientAnalysis = ({ orders, assignments, clients }) => {
  const analysisData = useMemo(() => {
    const clientStats = new Map();

    // Procesar datos por cliente
    orders.forEach(order => {
      const clientId = order.clientId;
      if (!clientStats.has(clientId)) {
        const client = clients.find(c => c.ClientID === clientId);
        clientStats.set(clientId, {
          id: clientId,
          name: client ? client.ClientName : `Cliente ${clientId}`,
          totalInvestment: 0,
          totalAssigned: 0,
          ordersCount: 0,
          avgOrderValue: 0,
          lastOrderDate: null
        });
      }

      const stats = clientStats.get(clientId);
      stats.totalInvestment += parseFloat(order.netInvestment) || 0;
      stats.ordersCount += 1;
      
      const orderDate = new Date(order.dateFrom);
      if (!stats.lastOrderDate || orderDate > stats.lastOrderDate) {
        stats.lastOrderDate = orderDate;
      }
    });

    // Agregar asignaciones
    assignments.forEach(assignment => {
      const order = orders.find(o => o.id === assignment.insertionOrderId);
      if (!order) return;

      const clientId = order.clientId;
      if (clientStats.has(clientId)) {
        let amount = parseFloat(assignment.montoValor) || 0;
        if (assignment.amountType === 'percentage') {
          amount = (amount * parseFloat(order.netInvestment)) / 100;
        }
        clientStats.get(clientId).totalAssigned += amount;
      }
    });

    // Calcular métricas finales
    return Array.from(clientStats.values())
      .map(client => ({
        ...client,
        avgOrderValue: client.ordersCount > 0 ? client.totalInvestment / client.ordersCount : 0,
        utilization: client.totalInvestment > 0 ? (client.totalAssigned / client.totalInvestment) * 100 : 0,
        daysSinceLastOrder: client.lastOrderDate ? Math.floor((new Date() - client.lastOrderDate) / (1000 * 60 * 60 * 24)) : 0
      }))
      .sort((a, b) => b.totalInvestment - a.totalInvestment);
  }, [orders, assignments, clients]);

  const insights = useMemo(() => {
    const totalClients = analysisData.length;
    const activeClients = analysisData.filter(c => c.daysSinceLastOrder <= 30).length;
    const highValueClients = analysisData.filter(c => c.totalInvestment > 50000).length;
    const overBudgetClients = analysisData.filter(c => c.utilization > 100).length;
    
    return {
      totalClients,
      activeClients,
      highValueClients,
      overBudgetClients,
      avgOrderValue: analysisData.reduce((sum, c) => sum + c.avgOrderValue, 0) / (totalClients || 1),
      avgUtilization: analysisData.reduce((sum, c) => sum + c.utilization, 0) / (totalClients || 1)
    };
  }, [analysisData]);

  const getClientStatus = (client) => {
    if (client.utilization > 100) return { color: 'danger', text: 'Sobre presupuesto' };
    if (client.daysSinceLastOrder > 60) return { color: 'warning', text: 'Inactivo' };
    if (client.totalInvestment > 50000) return { color: 'success', text: 'Alto valor' };
    return { color: 'info', text: 'Normal' };
  };

  return (
    <Card className="h-100">
      <Card.Header>
        <Flex justifyContent="between" alignItems="center">
          <h6 className="mb-0">Análisis de Clientes</h6>
          <FontAwesomeIcon icon="chart-bar" className="text-primary" />
        </Flex>
      </Card.Header>
      <Card.Body className="p-0">
        {/* Métricas de resumen */}
        <div className="p-3 border-bottom">
          <Row className="g-2">
            <Col xs={6}>
              <div className="text-center">
                <h5 className="mb-1 text-primary">{insights.totalClients}</h5>
                <small className="text-muted">Total Clientes</small>
              </div>
            </Col>
            <Col xs={6}>
              <div className="text-center">
                <h5 className="mb-1 text-success">{insights.activeClients}</h5>
                <small className="text-muted">Activos</small>
              </div>
            </Col>
            <Col xs={6}>
              <div className="text-center">
                <h5 className="mb-1 text-warning">${(insights.avgOrderValue / 1000).toFixed(0)}K</h5>
                <small className="text-muted">Orden Promedio</small>
              </div>
            </Col>
            <Col xs={6}>
              <div className="text-center">
                <h5 className="mb-1 text-info">{insights.avgUtilization.toFixed(0)}%</h5>
                <small className="text-muted">Utilización Prom.</small>
              </div>
            </Col>
          </Row>
        </div>

        {/* Lista de clientes */}
        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
          <ListGroup variant="flush">
            {analysisData.slice(0, 10).map((client, index) => {
              const status = getClientStatus(client);
              return (
                <ListGroup.Item key={client.id} className="px-3 py-2">
                  <Flex alignItems="center" justifyContent="between">
                    <div className="flex-1">
                      <h6 className="mb-1 fs-11 fw-semibold">
                        {client.name}
                        <Badge bg={status.color} className="ms-2 fs-11">
                          {status.text}
                        </Badge>
                      </h6>
                      <div className="d-flex justify-content-between">
                        <small className="text-muted">
                          ${(client.totalInvestment / 1000).toFixed(1)}K • {client.ordersCount} órdenes
                        </small>
                        <small className={`text-${status.color}`}>
                          {client.utilization.toFixed(0)}% utilización
                        </small>
                      </div>
                    </div>
                  </Flex>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        </div>

        {/* Alertas */}
        {insights.overBudgetClients > 0 && (
          <div className="p-3 bg-danger-subtle border-top">
            <div className="d-flex align-items-center">
              <FontAwesomeIcon icon="exclamation-triangle" className="text-danger me-2" />
              <small className="text-danger fw-semibold">
                {insights.overBudgetClients} cliente(s) sobre presupuesto
              </small>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default ClientAnalysis; 